import { ethers } from "ethers";
import { POLYGON_AMOY } from "./contracts/config";

declare global {
  interface Window {
    ethereum?: any;
  }
}

export const getProvider = () => {
  if (typeof window !== "undefined" && window.ethereum) {
    return new ethers.BrowserProvider(window.ethereum);
  }
  return null;
};

export const getSigner = async (provider: ethers.BrowserProvider) => {
  return await provider.getSigner();
};

export const switchToPolygonAmoy = async () => {
  if (!window.ethereum) {
    throw new Error("MetaMask is not installed");
  }

  try {
    // Try to switch to Polygon Amoy
    await window.ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: POLYGON_AMOY.chainId }],
    });
  } catch (switchError: any) {
    // This error code indicates that the chain has not been added to MetaMask
    if (switchError.code === 4902 || switchError.code === -32603) {
      try {
        // Add Polygon Amoy network to MetaMask
        await window.ethereum.request({
          method: "wallet_addEthereumChain",
          params: [POLYGON_AMOY],
        });
        // Wait for network to be added
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (addError: any) {
        if (addError.code === 4001) {
          throw new Error("Please approve adding Polygon Amoy network in MetaMask");
        }
        throw new Error("Failed to add Polygon Amoy network: " + (addError.message || "Unknown error"));
      }
    } else if (switchError.code === 4001) {
      throw new Error("Please approve network switch in MetaMask");
    } else {
      throw new Error("Failed to switch network: " + (switchError.message || "Unknown error"));
    }
  }
};

export const connectWallet = async () => {
  if (!window.ethereum) {
    throw new Error("MetaMask is not installed. Please install MetaMask extension.");
  }

  try {
    // First, switch to Polygon Amoy network
    await switchToPolygonAmoy();
    
    // Wait a bit for network to switch
    await new Promise(resolve => setTimeout(resolve, 500));

    // Then request account access
    const accounts = await window.ethereum.request({
      method: "eth_requestAccounts",
    });

    if (!accounts || accounts.length === 0) {
      throw new Error("No accounts found. Please unlock MetaMask.");
    }

    const provider = getProvider();
    if (!provider) {
      throw new Error("Failed to get provider");
    }

    // Verify we're on the correct network
    const network = await provider.getNetwork();
    const expectedChainId = BigInt(80002); // Polygon Amoy
    
    if (network.chainId !== expectedChainId) {
      // Try switching again
      await switchToPolygonAmoy();
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    const signer = await getSigner(provider);
    const address = await signer.getAddress();

    return { address, provider, signer };
  } catch (error: any) {
    // Better error messages
    if (error.code === 4001) {
      throw new Error("Please connect to MetaMask");
    } else if (error.code === -32002) {
      throw new Error("MetaMask request already pending. Please check MetaMask.");
    } else if (error.message?.includes("account")) {
      throw new Error("Please unlock MetaMask and try again");
    }
    throw new Error(error.message || "Failed to connect wallet");
  }
};

export const getCurrentAccount = async () => {
  if (!window.ethereum) {
    return null;
  }

  try {
    const accounts = await window.ethereum.request({
      method: "eth_accounts",
    });

    if (accounts.length === 0) {
      return null;
    }

    const provider = getProvider();
    if (!provider) {
      return null;
    }

    const signer = await getSigner(provider);
    const address = await signer.getAddress();

    return { address, provider, signer };
  } catch (error) {
    return null;
  }
};

export const getNetwork = async () => {
  if (!window.ethereum) {
    return null;
  }

  try {
    const chainId = await window.ethereum.request({
      method: "eth_chainId",
    });
    return chainId;
  } catch (error) {
    return null;
  }
};

export const formatAddress = (address: string) => {
  if (!address) return "";
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

export const formatEther = (value: bigint | string) => {
  return ethers.formatEther(value);
};

export const parseEther = (value: string) => {
  return ethers.parseEther(value);
};

